import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { Field, Notice, StateSelect, StepCard, StepTitle, UploadButton } from '../../components/ui'
import { createEmptyIndividual } from '../../lib/factories'
import { INDIVIDUAL_IMPORT_FIELDS, downloadCsvTemplate, readImportFile } from '../../lib/importRecords'
import type { BusinessEntity, IndividualLicensee } from '../../types/onboarding'

const headCellSx = { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', px: 1, py: 1.25, whiteSpace: 'nowrap' } as const
const cellSx = { px: 1, py: 1, verticalAlign: 'top' } as const

/** Design manual p.6 — Individual Affiliated Licensees (per entity). */
export function EntityIndividuals({ entity }: { entity: BusinessEntity }) {
  const { dispatch } = useOnboarding()
  const [importResult, setImportResult] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null)

  const update = (individualId: string, patch: Partial<Omit<IndividualLicensee, 'id'>>) =>
    dispatch({ type: 'UPDATE_INDIVIDUAL', entityId: entity.id, individualId, patch })

  const onImport = async (files: File[]) => {
    try {
      const records = await readImportFile(files[0], INDIVIDUAL_IMPORT_FIELDS, 'individual')
      const individuals = records
        .filter((r) => Object.values(r).some((v) => v.length > 0))
        .map((r) => createEmptyIndividual({ ...r, residentState: r.residentState.toUpperCase().slice(0, 2) }))
      if (individuals.length === 0) {
        setImportResult({ tone: 'warning', text: `No individuals were found in ${files[0].name}.` })
        return
      }
      dispatch({ type: 'ADD_INDIVIDUALS', entityId: entity.id, individuals })
      setImportResult({ tone: 'success', text: `Imported ${individuals.length} individual${individuals.length === 1 ? '' : 's'} from ${files[0].name}.` })
    } catch (err) {
      setImportResult({ tone: 'warning', text: err instanceof Error ? err.message : 'The file could not be read.' })
    }
  }

  return (
    <StepCard>
      <StepTitle
        subtitle={`Please identify all licensed individuals affiliated with ${entity.name || 'this entity'} whose surplus lines compliance will be managed by MISC.`}
      >
        Individual Affiliated Licensees
      </StepTitle>

      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => dispatch({ type: 'ADD_INDIVIDUALS', entityId: entity.id, individuals: [createEmptyIndividual()] })}
          >
            Add Individual
          </Button>
          <UploadButton label="Import CSV" accept=".csv,text/csv" onFiles={onImport} />
          <Button
            type="button"
            size="small"
            onClick={() => downloadCsvTemplate('individual-licensees-template.csv', ['Full Name', 'Resident State', 'License Number', 'NPN'])}
          >
            Download CSV template
          </Button>
        </Box>

        {importResult ? <Notice tone={importResult.tone}>{importResult.text}</Notice> : null}
        {entity.individuals.length === 0 ? (
          <Notice tone="neutral">If this entity has no licensed individuals other than its DRLP(s), you can continue.</Notice>
        ) : null}

        <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={headCellSx}>Full Name (exactly as appears on license)</TableCell>
                <TableCell sx={headCellSx}>Resident State</TableCell>
                <TableCell sx={headCellSx}>License Number</TableCell>
                <TableCell sx={headCellSx}>NPN</TableCell>
                <TableCell sx={headCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entity.individuals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                    No individuals added yet. Add each licensed individual or import a CSV.
                  </TableCell>
                </TableRow>
              ) : null}
              {entity.individuals.map((p) => (
                <TableRow key={p.id}>
                  <TableCell sx={{ ...cellSx, minWidth: 260 }}>
                    <Field value={p.fullName} inputProps={{ 'aria-label': 'Full name' }} onChange={(e) => update(p.id, { fullName: e.target.value })} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 170 }}>
                    <StateSelect value={p.residentState} inputProps={{ 'aria-label': 'Resident state' }} onChange={(code) => update(p.id, { residentState: code })} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 150 }}>
                    <Field value={p.licenseNumber} inputProps={{ 'aria-label': 'License number' }} onChange={(e) => update(p.id, { licenseNumber: e.target.value })} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 130 }}>
                    <Field value={p.npn} inputProps={{ 'aria-label': 'NPN', inputMode: 'numeric' }} onChange={(e) => update(p.id, { npn: e.target.value })} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title="Delete individual">
                      <IconButton
                        aria-label={`Delete ${p.fullName || 'individual'}`}
                        onClick={() => dispatch({ type: 'REMOVE_INDIVIDUAL', entityId: entity.id, individualId: p.id })}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </StepCard>
  )
}
